import React, { useMemo, useState } from 'react';
import { Clock, Timer } from 'lucide-react';
import type { Todo, TimeBlock } from './types';

interface TimeboxedDayProps {
  todos: Todo[];
}

const parseTimeEstimate = (estimate: string): number => {
  const cleanEstimate = estimate.trim().toLowerCase();
  
  // Handle hours and minutes format (e.g., "1h30" or "1h30m")
  if (cleanEstimate.includes('h')) {
    const parts = cleanEstimate.split('h');
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]?.replace('m', '')) || 0;
    return (hours * 60) + minutes;
  }
  
  // Handle minutes only format (e.g., "45m" or "45")
  return parseInt(cleanEstimate.replace('m', '')) || 0;
};

const formatDuration = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h${remainingMinutes}m` : `${hours}h`;
};

const parseTimeString = (timeStr: string): { hours: number; minutes: number } => {
  const [time, period] = timeStr.split(' ');
  let [hours, minutes = '0'] = time.split(':');
  let hour = parseInt(hours);
  
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  
  return { hours: hour, minutes: parseInt(minutes) };
};

export function TimeboxedDay({ todos }: TimeboxedDayProps) {
  const [workEndTime, setWorkEndTime] = useState('6:00 PM');

  const currentTime = new Date();
  const WORK_START_HOUR = currentTime.getHours();
  const { hours: WORK_END_HOUR } = parseTimeString(workEndTime);
  const TOTAL_HOURS = WORK_END_HOUR - WORK_START_HOUR;

  const timeBlocks = useMemo(() => {
    const blocks: TimeBlock[] = [];
    let currentTime = new Date();
    currentTime.setHours(WORK_START_HOUR, 0, 0, 0);

    const uncompletedTodos = todos.filter(todo => !todo.completed);
    let lastEndTime = currentTime;

    for (const todo of uncompletedTodos) {
      const duration = parseTimeEstimate(todo.timeEstimate);
      const taskStartTime = new Date(lastEndTime);
      const taskEndTime = new Date(taskStartTime.getTime() + duration * 60000);

      blocks.push({
        todo,
        startTime: taskStartTime,
        endTime: taskEndTime,
        duration
      });

      lastEndTime = taskEndTime;
    }

    return blocks;
  }, [todos, WORK_START_HOUR]);

  return (
    <div className="w-full bg-[#0a0a0a] border-t border-[#404040]">
      <div className="px-4">
        <div className="flex items-center justify-between text-[#a0a0a0] py-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <h2 className="text-sm">Schedule</h2>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span>Day Ends</span>
              <select
                value={workEndTime}
                onChange={(e) => setWorkEndTime(e.target.value)}
                className="bg-[#0a0a0a] text-[#a0a0a0] border border-[#404040] px-2 py-0.5 rounded text-xs terminal-input"
              >
                {Array.from({ length: 12 }).map((_, i) => (
                  <option key={i} value={`${i + 1}:00 ${i + 1 < 11 ? 'PM' : 'AM'}`}>
                    {`${i + 1}:00 ${i + 1 < 11 ? 'PM' : 'AM'}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="relative bg-[#0a0a0a] overflow-hidden" style={{ height: "100px" }}>
        {/* Hour markers */}
        <div className="absolute inset-0 flex">
          {Array.from({ length: TOTAL_HOURS }).map((_, i) => {
            const hour = WORK_START_HOUR + i;
            const width = `${(1 / TOTAL_HOURS) * 100}%`;
            
            return (
              <div
                key={i}
                className="relative flex flex-col h-full border-l border-[#404040]"
                style={{ width }}
              >
                <div className="px-2 text-[#606060] text-xs leading-6">
                  {(hour % 12 || 12) + (hour >= 12 ? 'p' : 'a')}
                </div>
                {/* 15-minute markers */}
                <div className="flex-1 flex">
                  {[0, 15, 30, 45].map((minute) => (
                    <div
                      key={minute}
                      className="flex-1 border-l border-[#404040]/30"
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Time blocks */}
        <div className="absolute inset-x-0 bottom-0">
          {timeBlocks.map((block, index) => {
            const startMinutes = (block.startTime.getHours() - WORK_START_HOUR) * 60 + block.startTime.getMinutes();
            const durationMinutes = block.duration;
            const left = `${(startMinutes / (TOTAL_HOURS * 60)) * 100}%`;
            const width = `${(durationMinutes / (TOTAL_HOURS * 60)) * 100}%`;

            const difficultyColors = {
              0: { // Easy
                bg: 'bg-green-500/10',
                border: 'border-green-500/30',
                glow: 'shadow-[0_0_15px_rgba(34,197,94,0.2)]',
                text: 'text-green-200'
              },
              1: { // Medium
                bg: 'bg-orange-500/10',
                border: 'border-orange-500/30',
                glow: 'shadow-[0_0_15px_rgba(249,115,22,0.2)]',
                text: 'text-orange-200'
              },
              2: { // Hard
                bg: 'bg-red-500/10',
                border: 'border-red-500/30',
                glow: 'shadow-[0_0_15px_rgba(239,68,68,0.2)]',
                text: 'text-red-200'
              }
            };

            const colors = difficultyColors[block.todo.difficulty];

            return (
              <div
                key={`${block.todo.id}-${index}`}
                className={`absolute h-8 ${colors.bg} ${colors.border} ${colors.glow} border backdrop-blur-sm rounded-md transition-all duration-300 group hover:scale-[1.02] hover:z-10`}
                style={{ 
                  left,
                  width,
                  bottom: "8px"
                }}
              >
                <div className="flex items-center justify-between h-full px-3 overflow-hidden gap-2">
                  <div className="flex-1 text-sm text-[#a0a0a0] group-hover:text-white truncate transition-colors">
                    {block.todo.text}
                  </div>
                  <div className={`flex items-center gap-1.5 text-xs ${colors.text}`}>
                    <Timer className="w-3 h-3" />
                    <span>{formatDuration(block.duration)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}